import React, { useState, useEffect, useCallback } from 'react';
import { X, CalendarDays, ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { api, inr } from '../api';
import { toast } from 'sonner';

const istMonth = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 7);

const shiftMonth = (ym, n) => {
  const d = new Date(ym + '-01T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 7);
};

const STATUS_META = {
  present: { label: 'Present', cls: 'bg-emerald-100 text-emerald-700' },
  leave: { label: 'On Leave', cls: 'bg-sky-100 text-sky-700' },
  absent: { label: 'Absent', cls: 'bg-slate-200 text-slate-500' },
};

function DayEditor({ agentId, day, onSaved, onClose }) {
  const [status, setStatus] = useState(day.status || (day.counted ? 'present' : 'absent'));
  const [adj, setAdj] = useState(day.adjustment || '');
  const [reason, setReason] = useState(day.adj_reason || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/points-day', {
        agent_id: agentId, ist_date: day.date, status,
        adjustment: adj === '' ? 0 : Number(adj), reason,
      });
      toast.success(`${day.date} updated — ${STATUS_META[status].label}${Number(adj) ? `, ${Number(adj) > 0 ? '+' : ''}${adj} pts adjustment` : ''}`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 bg-amber-50/70 border-2 border-amber-200 rounded-xl p-3" data-testid="day-editor">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-extrabold text-slate-800">
          {new Date(day.date + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          <span className="font-normal text-slate-400 ml-2">sales {inr(day.sales)} · auto {day.auto_points >= 0 ? '+' : ''}{day.auto_points} pts</span>
        </p>
        <button onClick={onClose} className="p-1 rounded-full hover:bg-amber-100 text-slate-400"><X size={14} /></button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {Object.entries(STATUS_META).map(([k, v]) => (
            <button key={k} data-testid={`day-status-${k}`} onClick={() => setStatus(k)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-bold border-2 transition-colors ${status === k ? 'border-slate-900 ' + v.cls : 'border-transparent bg-white text-slate-400 hover:text-slate-600'}`}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          <input data-testid="day-adjust-points" type="number" placeholder="± pts" value={adj}
            onChange={(e) => setAdj(e.target.value)}
            className="w-20 bg-white border border-amber-300 rounded-lg px-2 py-1 text-xs font-bold focus:outline-none" />
          <input data-testid="day-adjust-reason" placeholder="Reason (e.g. approved half-day)" value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-44 bg-white border border-amber-300 rounded-lg px-2 py-1 text-xs focus:outline-none" />
          <button data-testid="day-editor-save" onClick={save} disabled={saving}
            className="flex items-center gap-1 bg-slate-900 text-white rounded-lg px-3 py-1.5 text-[11px] font-bold hover:bg-slate-700 disabled:opacity-50">
            <Save size={11} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
      <p className="font-hand text-sm text-amber-700 mt-1.5">On Leave / Absent days are skipped — no target, no negative points. Adjustments add/subtract points for that day.</p>
    </div>
  );
}

export default function AdminPointsCalendar({ onClose }) {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [month, setMonth] = useState(istMonth());
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get('/brownie').then((r) => {
      setAgents(r.data);
      if (r.data.length) setAgentId(r.data[0].agent_id);
    });
  }, []);

  const load = useCallback(() => {
    if (!agentId) return;
    setSelected(null);
    api.get('/admin/points-calendar', { params: { agent_id: agentId, month } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load calendar'));
  }, [agentId, month]);

  useEffect(() => { load(); }, [load]);

  const firstDow = data ? (new Date(data.month + '-01T00:00:00').getDay() + 6) % 7 : 0;
  const monthLabel = new Date(month + '-01T00:00:00').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" onClick={onClose} data-testid="points-calendar-overlay">
      <div className="bg-white rounded-2xl sticky-shadow w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="points-calendar-modal">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100 flex-wrap gap-2">
          <h3 className="font-display font-extrabold text-lg text-slate-900 flex items-center gap-2">
            <CalendarDays size={18} className="text-amber-500" /> Points Calendar
          </h3>
          <div className="flex items-center gap-2">
            <select data-testid="calendar-agent-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}
              className="bg-white border-2 border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-bold text-slate-800 focus:outline-none cursor-pointer">
              {agents.map((a) => <option key={a.agent_id} value={a.agent_id}>{a.name}</option>)}
            </select>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg px-1 py-0.5">
              <button data-testid="calendar-prev-month" onClick={() => setMonth(shiftMonth(month, -1))} className="p-1 rounded hover:bg-white text-slate-500"><ChevronLeft size={14} /></button>
              <span className="text-xs font-bold text-slate-700 w-32 text-center" data-testid="calendar-month-label">{monthLabel}</span>
              <button data-testid="calendar-next-month" onClick={() => setMonth(shiftMonth(month, 1))} disabled={month >= istMonth()}
                className="p-1 rounded hover:bg-white text-slate-500 disabled:opacity-25"><ChevronRight size={14} /></button>
            </div>
            <button data-testid="points-calendar-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin px-5 py-4">
          {!data ? (
            <p className="font-hand text-xl text-slate-400 text-center py-8">Loading calendar…</p>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap mb-3 text-[11px] font-bold">
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1" data-testid="calendar-total-final">
                  🍫 Month total: {data.totals.final >= 0 ? '+' : ''}{data.totals.final.toLocaleString('en-IN')} pts
                </span>
                <span className="bg-slate-50 text-slate-600 border border-slate-200 rounded-full px-3 py-1">
                  auto {data.totals.auto >= 0 ? '+' : ''}{data.totals.auto} · adjustments {data.totals.adjustments >= 0 ? '+' : ''}{data.totals.adjustments}
                </span>
                <span className="bg-slate-50 text-slate-600 border border-slate-200 rounded-full px-3 py-1">{data.totals.days_counted} days counted · sales {inr(data.totals.sales)}</span>
                {data.target > 0
                  ? <span className="bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-3 py-1">target {inr(data.target)}/day @ {data.brownie_pct}%</span>
                  : <span className="bg-red-50 text-red-500 border border-red-200 rounded-full px-3 py-1">no target set — days score 0</span>}
              </div>

              <div className="grid grid-cols-7 gap-1.5 text-center mb-1">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                  <p key={d} className="text-[9px] font-extrabold uppercase tracking-wider text-slate-400">{d}</p>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1.5" data-testid="points-calendar-grid">
                {Array.from({ length: firstDow }).map((_, i) => <div key={`e${i}`} />)}
                {data.days.map((d) => {
                  const excluded = d.status === 'leave' || d.status === 'absent';
                  const isSel = selected?.date === d.date;
                  return (
                    <button key={d.date} data-testid={`calendar-day-${d.date}`}
                      onClick={() => !d.future && setSelected(isSel ? null : d)}
                      disabled={d.future}
                      className={`rounded-lg border-2 p-1.5 min-h-[58px] text-left transition-all relative
                        ${d.future ? 'border-slate-100 bg-slate-50/50 opacity-40 cursor-default'
                          : isSel ? 'border-slate-900 bg-amber-50'
                          : excluded ? 'border-sky-200 bg-sky-50/70 hover:border-sky-400'
                          : d.counted ? (d.final >= 0 ? 'border-emerald-200 bg-emerald-50/60 hover:border-emerald-400' : 'border-red-200 bg-red-50/60 hover:border-red-400')
                          : 'border-slate-150 bg-white hover:border-slate-300'}`}>
                      <p className="text-[10px] font-extrabold text-slate-500">{Number(d.date.slice(-2))}</p>
                      {!d.future && (excluded ? (
                        <p className="text-[9px] font-bold text-sky-600 mt-0.5">{d.status === 'leave' ? '🏖 Leave' : '— Absent'}</p>
                      ) : d.counted ? (
                        <>
                          <p className={`text-[10px] font-extrabold ${d.final >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{d.final >= 0 ? '+' : ''}{d.final}</p>
                          {d.sales > 0 && <p className="text-[8px] text-slate-400 truncate">{inr(d.sales)}</p>}
                        </>
                      ) : (
                        <p className="text-[9px] text-slate-300 mt-0.5">not in</p>
                      ))}
                      {d.adjustment !== 0 && !d.future && (
                        <span className="absolute top-1 right-1 text-[8px] font-extrabold text-violet-600" title={d.adj_reason}>✎{d.adjustment > 0 ? '+' : ''}{d.adjustment}</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {selected && (
                <DayEditor agentId={agentId} day={selected}
                  onSaved={() => { setSelected(null); load(); }}
                  onClose={() => setSelected(null)} />
              )}
              {!selected && <p className="font-hand text-base text-slate-400 mt-3 text-center">click any past day to mark Leave/Absent or adjust its points ✏️</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
